import type { AddToPurchaseListStrategy } from '@/components/purchase-order/AddToPurchaseListDialog';
import {
  buildInitialGroupsFromSelected,
  hasFullSuggestionCoverage,
  mergeSuggestions,
} from '@/components/purchase-order/AddToPurchaseListDialog';
import { fetchSupplierProductExpansion } from '@/utils/purchase-order/fetchSupplierProductExpansion';

export interface SupplierProductsStrategyInput {
  supplierId: number;
  selectedRows: any[]; // rows from SupplierProductList; variant-level
}

export function supplierProductsStrategy(
  input: SupplierProductsStrategyInput,
): AddToPurchaseListStrategy {
  const initialGroups = buildInitialGroupsFromSelected(input.selectedRows);

  const variantIds = Array.from(new Set(
    (input.selectedRows || [])
      .map((r) => Number(r?.variant_id))
      .filter((n): n is number => Number.isFinite(n)),
  ));
  const enrich = async () => {
    const { rows } = await fetchSupplierProductExpansion({
      supplierId: input.supplierId,
      variantIds,
    });
    return rows;
  };

  // Case A: every selected row already carries [inventory_replenishment].
  // Render immediately; defer enrich to submit time so it only fires if
  // a variant_id needs recovery (Supplier Products always has finite
  // variant_ids today, so on the happy path no network call fires).
  if (hasFullSuggestionCoverage(input.selectedRows)) {
    return {
      initialGroups,
      enrich,
      mergeEnrichment: mergeSuggestions,
      enrichmentRequired: false,
      enrichAtSubmit: true,
    };
  }

  return {
    // Render selected rows immediately — variant-level catalog rows already
    // carry variant_id / productName / variant / sku / cost, so we don't need
    // to wait on the fetch. Same fast-path UX as the Report page's by_variant.
    initialGroups,
    enrich,
    // Selection-overlay: mergeSuggestions keeps any group variant that the
    // enrichment didn't match (because variantId lookup misses). That matches
    // the "what I selected is what I see" rule in spec §3 decision 7.
    mergeEnrichment: mergeSuggestions,
    enrichmentRequired: false,
  };
}
